import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';

interface UseBatchTrackZapsOptions {
  aTags: string[];
  relays?: string[];
  enabled?: boolean;
}

/**
 * Fetch zap totals for multiple tracks at once
 * This is optimized for batch fetching to avoid sequential queries
 */
export function useBatchTrackZaps(options: UseBatchTrackZapsOptions) {
  const { aTags, relays, enabled = true } = options;
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['batch-track-zaps', aTags, relays],
    queryFn: async (c) => {
      if (aTags.length === 0) {
        return new Map<string, number>();
      }

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      // Connect to multiple relays for better discovery
      const relayList = relays || [
        'wss://relay.ditto.pub',
        'wss://relay.nostr.band',
        'wss://relay.damus.io',
        'wss://nos.lol',
      ];

      const relayGroup = nostr.group(relayList);

      // Fetch all zap receipts for these tracks
      const zapEvents = await relayGroup.query(
        [{
          kinds: [9735],
          '#a': aTags,
          limit: 1000,
        }],
        { signal }
      );

      // Calculate total zaps per track
      const zapTotals = new Map<string, number>();

      for (const zapEvent of zapEvents) {
        // Get the 'a' tag to identify which track this zap is for
        const aTag = zapEvent.tags.find(([name]) => name === 'a')?.[1];
        if (!aTag) continue;

        // Parse amount from bolt11
        const bolt11Tag = zapEvent.tags.find(([name]) => name === 'bolt11')?.[1];
        if (!bolt11Tag) continue;

        const amount = parseBolt11Amount(bolt11Tag);
        zapTotals.set(aTag, (zapTotals.get(aTag) || 0) + amount);
      }

      return zapTotals;
    },
    enabled: enabled && aTags.length > 0,
    // Cache zap data for 30 seconds
    staleTime: 30000,
    // Keep in cache for 5 minutes
    gcTime: 300000,
  });
}

/**
 * Simple bolt11 amount parser
 * Extracts the amount in millisats from a bolt11 invoice
 * Format: ln[bc|tb|bcrt][amount][multiplier]1[data]
 */
function parseBolt11Amount(bolt11: string): number {
  try {
    // Lightning invoices format: lnbc[amount][multiplier]1...
    // The "1" separates the human-readable part from the data part
    const match = bolt11.match(/ln(?:bc|tb|bcrt)([0-9]+)([munp]?)1/);
    if (!match) return 0;

    const amount = parseInt(match[1]);
    if (isNaN(amount) || amount <= 0) return 0;

    const multiplier = match[2];

    // Convert to millisats based on multiplier
    let millisats = 0;
    switch (multiplier) {
      case 'm': // milli-bitcoin (0.001 BTC)
        millisats = amount * 100_000_000; // 0.001 BTC = 100,000 sats = 100,000,000 millisats
        break;
      case 'u': // micro-bitcoin (0.000001 BTC)
        millisats = amount * 100_000; // 0.000001 BTC = 100 sats = 100,000 millisats
        break;
      case 'n': // nano-bitcoin (0.000000001 BTC)
        millisats = amount * 100; // 0.000000001 BTC = 0.1 sats = 100 millisats
        break;
      case 'p': // pico-bitcoin (0.000000000001 BTC)
        millisats = amount / 10; // 0.000000000001 BTC = 0.0001 sats = 0.1 millisats
        break;
      default:
        // No multiplier means the amount is in bitcoin
        millisats = amount * 100_000_000_000; // 1 BTC = 100,000,000 sats = 100,000,000,000 millisats
    }

    return Math.floor(millisats);
  } catch {
    return 0;
  }
}
